import type { ReactNode } from "react"

import { useTranslation } from "react-i18next"

import type { HostedAgentBudgetStatus, HostedAgentBudgetWindow } from "@/api/admin-api"

import { Skeleton } from "@/components/ui/skeleton"

import { useAdminHostedAgentUsageQuery } from "../hooks/admin-queries"

/**
 * Per-agent consumption panel (HERMES Fas 6): tokens/SEK for the last 30
 * days aggregated from the durable run-posts, plus the live budget-window
 * utilization from the detail read. Rendered under the edit wizard's Budget
 * tab (EditHostedAgentDialog).
 */
export function HostedAgentUsagePanel({
  agentId,
  budgetStatus,
}: {
  agentId: string
  budgetStatus: HostedAgentBudgetStatus | null
}) {
  const { t, i18n } = useTranslation()
  const usageQuery = useAdminHostedAgentUsageQuery(30)
  const usage = usageQuery.data?.agents.find((entry) => entry.agentId === agentId)
  const numberFormat = new Intl.NumberFormat(i18n.language)

  const allWindows: Array<{ key: string; label: string; status: HostedAgentBudgetWindow | null }> =
    budgetStatus
      ? [
          {
            key: "daily_tokens",
            label: t("adminDashboard.hostedAgents.usagePanel.windowDailyTokens", {
              defaultValue: "Daily tokens",
            }),
            status: budgetStatus.windows.daily_tokens,
          },
          {
            key: "monthly_tokens",
            label: t("adminDashboard.hostedAgents.usagePanel.windowMonthlyTokens", {
              defaultValue: "Monthly tokens",
            }),
            status: budgetStatus.windows.monthly_tokens,
          },
          {
            key: "monthly_sek",
            label: t("adminDashboard.hostedAgents.usagePanel.windowMonthlySek", {
              defaultValue: "Monthly SEK",
            }),
            status: budgetStatus.windows.monthly_sek,
          },
        ]
      : []
  const configuredWindows = allWindows.flatMap(({ key, label, status }) =>
    status ? [{ key, label, status }] : []
  )

  let usageContent: ReactNode
  if (usageQuery.isLoading) {
    usageContent = <Skeleton className="h-16 w-full" />
  } else if (usageQuery.isError) {
    usageContent = (
      <p className="text-muted-foreground text-sm">
        {t("adminDashboard.hostedAgents.usagePanel.loadError", {
          defaultValue: "Usage data is unavailable right now.",
        })}
      </p>
    )
  } else {
    usageContent = (
      <div className="grid grid-cols-2 gap-4 rounded-lg border p-3 sm:grid-cols-4">
        <div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.hostedAgents.usagePanel.runs", { defaultValue: "Runs" })}
          </div>
          <div className="text-lg font-semibold">{numberFormat.format(usage?.runs ?? 0)}</div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.hostedAgents.usagePanel.tokens", { defaultValue: "Tokens" })}
          </div>
          <div className="text-lg font-semibold">
            {numberFormat.format(usage?.totalTokens ?? 0)}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.hostedAgents.usagePanel.costSek", { defaultValue: "Cost (SEK)" })}
          </div>
          <div className="text-lg font-semibold">{(usage?.costSek ?? 0).toFixed(2)}</div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.hostedAgents.usagePanel.avgLatency", {
              defaultValue: "Avg latency",
            })}
          </div>
          <div className="text-lg font-semibold">
            {usage?.avgLatencyMs != null ? `${numberFormat.format(usage.avgLatencyMs)} ms` : "—"}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-6 space-y-3 border-t pt-4">
      <h4 className="text-sm font-medium">
        {t("adminDashboard.hostedAgents.usagePanel.title", {
          defaultValue: "Consumption (last 30 days)",
        })}
      </h4>
      {usageContent}

      {configuredWindows.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">
            {t("adminDashboard.hostedAgents.usagePanel.budgetTitle", {
              defaultValue: "Budget utilization",
            })}
          </h4>
          {configuredWindows.map(({ key, label, status }) => (
            <div key={key} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{label}</span>
                <span
                  className={
                    status.exceeded
                      ? "bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 font-medium"
                      : undefined
                  }
                >
                  {numberFormat.format(status.used)} / {numberFormat.format(status.limit)} (
                  {status.percent.toFixed(0)}%)
                </span>
              </div>
              <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
                <div
                  className={status.exceeded ? "bg-destructive h-full" : "bg-primary h-full"}
                  style={{ width: `${Math.min(100, status.percent)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
